import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Image as ImageIcon } from 'lucide-react';
import { heroSlidesService, HeroSlide } from '../../services/heroSlidesService';
import { getImageUrl } from '../../services/api';

const PUBLIC_PAGES = [
    { label: 'Accueil', path: '/' },
    { label: 'Présentation', path: '/Presentation' },
    { label: 'Actualité', path: '/Actualite' },
    { label: 'Événement', path: '/Evenement' },
    { label: 'Publication', path: '/Publication' },
    { label: 'Galerie', path: '/Galerie' },
    { label: 'Contact', path: '/Contact' },
    { label: 'Login', path: '/login' },
];

const HomepageContent: React.FC = () => {
    const [slides, setSlides] = useState<HeroSlide[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);

    // Form State
    const [formData, setFormData] = useState<Partial<HeroSlide>>({
        title: '',
        subtitle: '',
        description: '',
        image: '',
        ctaText: '',
        ctaLink: '',
        order: 0
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        fetchSlides();
    }, []);

    const fetchSlides = async () => {
        try {
            const data = await heroSlidesService.getAll();
            setSlides(data);
        } catch (error) {
            console.error("Error fetching slides:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (slide?: HeroSlide) => {
        setImageFile(null);
        setPreviewUrl(null);
        if (slide) {
            setEditingSlide(slide);
            setFormData(slide);
            // Construct full URL for preview
            setPreviewUrl(getImageUrl(slide.image));
        } else {
            setEditingSlide(null);
            setFormData({
                title: '',
                subtitle: '',
                description: '',
                image: '',
                ctaText: '',
                ctaLink: '',
                order: slides.length
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSlide(null);
        setImageFile(null);
        setPreviewUrl(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'order' ? parseInt(value) || 0 : value
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const data = new FormData();
            data.append('title', formData.title || '');
            data.append('subtitle', formData.subtitle || '');
            data.append('description', formData.description || '');
            data.append('ctaText', formData.ctaText || '');
            data.append('ctaLink', formData.ctaLink || '');
            data.append('order', (formData.order || 0).toString());

            if (imageFile) {
                data.append('image', imageFile);
            }

            if (editingSlide) {
                await heroSlidesService.update(editingSlide.id, data);
            } else {
                if (!imageFile) {
                    alert('Please select an image');
                    return;
                }
                await heroSlidesService.create(data);
            }
            fetchSlides();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving slide:", error);
            alert("Failed to save slide");
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Are you sure you want to delete this slide?")) {
            try {
                await heroSlidesService.delete(id);
                fetchSlides();
            } catch (error) {
                console.error("Error deleting slide:", error);
            }
        }
    };


    if (loading) return <div className="p-8">Loading...</div>;

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Accueil main banner</h1>
                    <p className="text-gray-500 mt-1">Ajouter vos images pour le main banner du site</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors font-medium"
                >
                    <Plus size={20} />
                    Add New Slide
                </button>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {slides.map((slide) => (
                    <div key={slide.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row hover:shadow-md transition-shadow">
                        <div className="w-full md:w-1/4 h-48 md:h-auto relative bg-gray-100">
                            {slide.image ? (
                                <img src={getImageUrl(slide.image)} alt={slide.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="flex items-center justify-center w-full h-full text-gray-400">
                                    <ImageIcon size={40} />
                                </div>
                            )}
                            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded backdrop-blur">
                                Order: {slide.order}
                            </div>
                        </div>
                        <div className="flex-1 p-6 flex flex-col justify-between">
                            <div>
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-sm font-semibold text-primary uppercase tracking-wider">{slide.subtitle}</span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleOpenModal(slide)}
                                            className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded transition-colors"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(slide.id)}
                                            className="p-1.5 text-gray-400 hover:text-error hover:bg-red-50 rounded transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{slide.title}</h3>
                                <p className="text-gray-600 line-clamp-2 mb-4">{slide.description}</p>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500 border-t pt-4 mt-2">
                                <span className="bg-gray-100 px-2 py-1 rounded">CTA: {slide.ctaText} → {slide.ctaLink}</span>
                            </div>
                        </div>
                    </div>
                ))}

                {slides.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-lg border border-dashed border-gray-300">
                        <ImageIcon size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">No slides found</h3>
                        <p className="text-gray-500 mb-6">Get started by creating your first hero slide</p>
                        <button
                            onClick={() => handleOpenModal()}
                            className="bg-primary-light/10 text-primary px-4 py-2 rounded-lg hover:bg-primary-light/20 transition-colors font-medium"
                        >
                            Create Slide
                        </button>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingSlide ? 'Edit Slide' : 'Create New Slide'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                                    <input
                                        type="text"
                                        name="subtitle"
                                        value={formData.subtitle}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Image</label>
                                <div className="flex flex-col gap-4">
                                    {previewUrl && (
                                        <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <div className="flex items-center justify-center w-full">
                                        <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <ImageIcon className="w-8 h-8 mb-4 text-gray-500" />
                                                <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                                <p className="text-xs text-gray-500">SVG, PNG, JPG or GIF (MAX. 5MB)</p>
                                            </div>
                                            <input id="dropzone-file" type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">CTA Text</label>
                                    <input
                                        type="text"
                                        name="ctaText"
                                        value={formData.ctaText}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">CTA Link</label>
                                    <select
                                        name="ctaLink"
                                        value={formData.ctaLink}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all bg-white"
                                        required
                                    >
                                        <option value="">Select a page</option>
                                        {PUBLIC_PAGES.map(page => (
                                            <option key={page.path} value={page.path}>{page.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">Order</label>
                                    <input
                                        type="number"
                                        name="order"
                                        value={formData.order}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t mt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark shadow-sm hover:shadow transition-all font-medium"
                                >
                                    <Save size={18} />
                                    Save Slide
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomepageContent;
